import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, AlertTriangle, XCircle, Clock,
  RefreshCw, Layers, Wifi, Server, CreditCard,
  MessageSquare, Shield, LayoutDashboard
} from 'lucide-react';
import api from '../utils/api';

// ─── CONFIG ───────────────────────────────────────────────────
const STATUS_CONFIG = {
  operational:   { label: 'Operational',    color: 'text-green-400',  bg: 'bg-green-500/10  border-green-500/20',  dot: 'bg-green-400',  icon: CheckCircle },
  degraded:      { label: 'Degraded',       color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20', dot: 'bg-yellow-400', icon: AlertTriangle },
  partial_outage:{ label: 'Partial Outage', color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20', dot: 'bg-orange-400', icon: AlertTriangle },
  major_outage:  { label: 'Major Outage',   color: 'text-red-400',    bg: 'bg-red-500/10    border-red-500/20',    dot: 'bg-red-400',    icon: XCircle },
};

const SEVERITY_CONFIG = {
  minor:    { label: 'Minor',    color: 'text-yellow-400', bg: 'bg-yellow-500/10 border-yellow-500/20' },
  major:    { label: 'Major',    color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20' },
  critical: { label: 'Critical', color: 'text-red-400',    bg: 'bg-red-500/10    border-red-500/20' },
};

const COMPONENT_ICONS = {
  'Game Servers':    Server,
  'Control Panel':  LayoutDashboard,
  'Billing System': CreditCard,
  'Support System': MessageSquare,
  'Network':        Wifi,
};

// ─── UPTIME BAR (90 days) ────────────────────────────────────
const UptimeBar = ({ uptime = 99.9, incidents = [] }) => {
  const days = 90;

  // Generate stable blocks based on uptime value — no randomness
  const blocks = Array.from({ length: days }, (_, i) => {
    // Check if there's an incident on this day
    const dayDate = new Date();
    dayDate.setDate(dayDate.getDate() - (days - i - 1));
    const dayStr = dayDate.toISOString().split('T')[0];

    const incident = incidents.find(inc => {
      const incDate = new Date(inc.created_at).toISOString().split('T')[0];
      return incDate === dayStr;
    });

    if (incident) {
      return incident.severity === 'critical' ? 'red' : 'yellow';
    }

    return 'green';
  });

  const colorMap = { green: 'bg-green-500', yellow: 'bg-yellow-500', red: 'bg-red-500' };

  return (
    <div>
      <div className="flex gap-0.5 items-end h-8">
        {blocks.map((color, i) => (
          <div
            key={i}
            className={`flex-1 rounded-sm ${colorMap[color]} opacity-80 hover:opacity-100`}
            style={{ height: color === 'green' ? '100%' : color === 'yellow' ? '60%' : '30%' }}
          />
        ))}
      </div>
      <div className="flex justify-between text-xs text-gray-600 mt-1">
        <span>90 days ago</span>
        <span className="text-gray-400 font-medium">{uptime}% uptime</span>
        <span>Today</span>
      </div>
    </div>
  );
};

// ─── COMPONENT ROW ────────────────────────────────────────────
const ComponentRow = ({ component, incidents = [] }) => {
  const cfg = STATUS_CONFIG[component.status] || STATUS_CONFIG.operational;
  const Icon = COMPONENT_ICONS[component.name] || Server;
  const StatusIcon = cfg.icon;

  return (
    <div className="border-b border-gray-800/50 last:border-0 py-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center">
            <Icon className="w-4 h-4 text-gray-400" />
          </div>
          <div>
            <div className="font-semibold text-white text-sm">{component.name}</div>
            <div className="text-gray-500 text-xs">{component.description}</div>
          </div>
        </div>
        <div className={`flex items-center gap-1.5 border rounded-full px-3 py-1 text-xs font-medium ${cfg.bg} ${cfg.color}`}>
          <StatusIcon className="w-3 h-3" />
          {cfg.label}
        </div>
      </div>
      <UptimeBar uptime={component.uptime_30d} incidents={incidents} />
    </div>
  );
};

// ─── INCIDENT CARD ────────────────────────────────────────────
const IncidentCard = ({ incident }) => {
  const sev = SEVERITY_CONFIG[incident.severity] || SEVERITY_CONFIG.minor;
  const isResolved = !!incident.resolved_at;

  return (
    <div className={`border rounded-2xl p-5 ${isResolved ? 'border-gray-800 opacity-70' : sev.bg + ' border'}`}>
      <div className="flex items-start justify-between gap-4 mb-2">
        <h3 className="font-bold text-white">{incident.title}</h3>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isResolved && (
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${sev.bg} ${sev.color}`}>
              {sev.label}
            </span>
          )}
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
            isResolved
              ? 'bg-green-500/10 text-green-400'
              : 'bg-orange-500/10 text-orange-400'
          }`}>
            {isResolved ? '✓ Resolved' : '● Active'}
          </span>
        </div>
      </div>
      <p className="text-gray-400 text-sm mb-3 leading-relaxed">{incident.message}</p>
      <div className="flex items-center gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Started: {new Date(incident.created_at).toLocaleString()}
        </span>
        {isResolved && (
          <span className="flex items-center gap-1 text-green-500">
            <CheckCircle className="w-3 h-3" />
            Resolved: {new Date(incident.resolved_at).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
};

// ─── OVERALL STATUS BANNER ────────────────────────────────────
const StatusBanner = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.operational;
  const Icon = cfg.icon;

  const messages = {
    operational:    'All systems are fully operational.',
    degraded:       'Some systems are experiencing issues.',
    partial_outage: 'Some services are currently unavailable.',
    major_outage:   'We are experiencing a major service outage.',
  };

  return (
    <div className={`border-2 rounded-2xl p-6 mb-8 ${cfg.bg}`}>
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
          status === 'operational' ? 'bg-green-500/20' : 'bg-red-500/20'
        }`}>
          <Icon className={`w-6 h-6 ${cfg.color}`} />
        </div>
        <div>
          <h2 className={`text-xl font-black ${cfg.color}`}>{cfg.label}</h2>
          <p className="text-gray-300 text-sm mt-0.5">{messages[status]}</p>
        </div>
        {status === 'operational' && (
          <div className="ml-auto hidden sm:flex items-center gap-2">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <span className="text-green-400 text-sm font-medium">Live</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── MAIN STATUS PAGE ─────────────────────────────────────────
const StatusPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // If accessed via main domain /status redirect to subdomain
  useEffect(() => {
    if (window.location.hostname === 'plarixmc.com' || window.location.hostname === 'www.plarixmc.com') {
      window.location.href = 'https://status.plarixmc.com';
    }
  }, []);

  const load = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const { data: d } = await api.get('/status');
      setData(d);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Status fetch failed:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(interval);
  }, []);

  const activeIncidents = data?.incidents?.filter(i => !i.resolved_at) || [];
  const resolvedIncidents = data?.incidents?.filter(i => i.resolved_at) || [];

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <a href="https://plarixmc.com" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-green-500 rounded-lg flex items-center justify-center">
              <Layers className="w-4 h-4 text-gray-950" />
            </div>
            <span className="text-white font-bold">Plarix<span className="text-green-400">MC</span></span>
            <span className="text-gray-600 text-sm ml-1">/ Status</span>
          </a>
          <button
            onClick={() => load(true)}
            disabled={refreshing}
            className="flex items-center gap-1.5 text-gray-400 hover:text-white text-sm transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {lastUpdated && <span className="hidden sm:block">Updated {lastUpdated.toLocaleTimeString()}</span>}
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        {loading ? (
          <div className="space-y-4 animate-pulse">
            <div className="h-24 bg-gray-900 border border-gray-800 rounded-2xl" />
            <div className="h-64 bg-gray-900 border border-gray-800 rounded-2xl" />
          </div>
        ) : (
          <>
            {/* Overall status */}
            <StatusBanner status={data?.status || 'operational'} />

            {/* Active incidents */}
            {activeIncidents.length > 0 && (
              <div className="mb-8">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
                  Active Incidents
                </h2>
                <div className="space-y-3">
                  {activeIncidents.map(i => <IncidentCard key={i.id} incident={i} />)}
                </div>
              </div>
            )}

            {/* Components */}
            <div className="bg-gray-900 border border-gray-800 rounded-2xl mb-8 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-800">
                <h2 className="font-bold text-white">System Components</h2>
              </div>
              <div className="px-6">
                {data?.components?.map(c => (
                  <ComponentRow key={c.name} component={c} incidents={data?.incidents || []} />
                ))}
              </div>
            </div>

            {/* Server stats */}
            {data?.stats && (
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 text-center">
                  <div className="text-3xl font-black text-green-400 mb-1">
                    {data.stats.servers_online}
                  </div>
                  <div className="text-gray-400 text-sm">Servers online</div>
                </div>
                <div className="bg-gray-900 border border-gray-800 rounded-2xl p-5 text-center">
                  <div className="text-3xl font-black text-white mb-1">
                    {data.stats.servers_total}
                  </div>
                  <div className="text-gray-400 text-sm">Total servers</div>
                </div>
              </div>
            )}

            {/* Past incidents */}
            {resolvedIncidents.length > 0 && (
              <div className="mb-8">
                <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
                  Past Incidents
                </h2>
                <div className="space-y-3">
                  {resolvedIncidents.map(i => <IncidentCard key={i.id} incident={i} />)}
                </div>
              </div>
            )}

            {/* No incidents message */}
            {data?.incidents?.length === 0 && (
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 text-center mb-8">
                <CheckCircle className="w-10 h-10 text-green-400 mx-auto mb-3" />
                <h3 className="font-bold text-white mb-1">No incidents in the last 7 days</h3>
                <p className="text-gray-500 text-sm">Everything has been running smoothly.</p>
              </div>
            )}

            {/* Footer */}
            <div className="text-center text-gray-600 text-xs space-y-1">
              <p>This page auto-refreshes every 60 seconds.</p>
              <p>
                Having an issue?{' '}
                <a href="https://plarixmc.com/tickets" className="text-green-400 hover:text-green-300 transition-colors">
                  Open a support ticket
                </a>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StatusPage;
